import { useEffect, useState } from "react";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, Search } from "lucide-react";
import useCotacaoUser from "./useCotacaoUser";

const emptyForm = { codigo: "", nome: "", marca: "", referencia: "", unidade: "", observacoes: "" };

export default function CotacaoProdutosPage() {
  const { canWrite, canDelete, isAuditor } = useCotacaoUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get("/cotacao/produtos");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const abrirNovo = () => { setForm(emptyForm); setEditingId(null); setDialogOpen(true); };
  const abrirEdit = (p) => {
    const clean = Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v ?? ""]));
    setForm({ ...emptyForm, ...clean });
    setEditingId(p.id);
    setDialogOpen(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await cotAxios.put(`/cotacao/produtos/${editingId}`, form);
      else await cotAxios.post("/cotacao/produtos", form);
      toast.success(editingId ? "Produto atualizado" : "Produto criado");
      setDialogOpen(false);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const excluir = async () => {
    try {
      await cotAxios.delete(`/cotacao/produtos/${delTarget.id}`);
      toast.success("Excluído");
      setDelOpen(false); setDelTarget(null);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const filtered = rows.filter((r) => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return [r.codigo, r.nome, r.marca, r.referencia, r.unidade].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="cot-produtos-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-6 h-6 text-blue-700" /> Produtos</h1>
          <p className="text-sm text-slate-500">Total: {rows.length}{isAuditor && " · modo somente leitura (Auditor)"}</p>
        </div>
        {canWrite && (
          <Button onClick={abrirNovo} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-novo-produto">
            <Plus className="w-4 h-4 mr-1" /> Novo produto
          </Button>
        )}
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input placeholder="Buscar por código, nome, marca..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="busca-produto" />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum produto cadastrado.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((p) => (
            <Card key={p.id} data-testid={`produto-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      {p.codigo && <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{p.codigo}</span>}
                      <div className="font-semibold">{p.nome}</div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex gap-4 flex-wrap">
                      {p.marca && <span>Marca: {p.marca}</span>}
                      {p.referencia && <span>Ref: {p.referencia}</span>}
                      {p.unidade && <span>Un: {p.unidade}</span>}
                    </div>
                    {p.observacoes && <div className="text-xs text-slate-500 mt-1">{p.observacoes}</div>}
                  </div>
                  <div className="flex gap-1">
                    {canWrite && (
                      <Button size="sm" variant="outline" onClick={() => abrirEdit(p)} data-testid={`btn-edit-p-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="destructive" onClick={() => { setDelTarget(p); setDelOpen(true); }} data-testid={`btn-del-p-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-produto">
          <DialogHeader><DialogTitle>{editingId ? "Editar produto" : "Novo produto"}</DialogTitle><DialogDescription>Informe pelo menos o nome do produto.</DialogDescription></DialogHeader>
          <form onSubmit={salvar} className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1"><Label>Código</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Nome*</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="in-nome-prod" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Marca</Label><Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} /></div>
              <div><Label>Referência</Label><Input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} /></div>
              <div><Label>Unidade</Label><Input value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} placeholder="ex: un, kg, cx" /></div>
            </div>
            <div><Label>Observações</Label><Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-salvar-produto">{saving ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Excluir */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Excluir produto</DialogTitle><DialogDescription>Confirme a exclusão do produto selecionado.</DialogDescription></DialogHeader>
          <div className="text-sm">Excluir <b>{delTarget?.nome}</b>?</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={excluir} data-testid="btn-confirmar-excluir-prod">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
